import {
  TChangeTemplate,
  TTemplateCreationInputConfig,
  TTemplateEntityReference,
  TTemplateInputValue,
} from '../models';

// Resolves the single supported placeholder syntax `{{input:inputId}}`. No
// expressions, evaluation, nested tokens, conditionals, loops or HTML — inserted
// values are always treated as plain text.
const TOKEN_PATTERN = /\{\{input:([a-zA-Z0-9_]+)\}\}/g;

export type TResolveMode = 'preview' | 'create';

const isEntityReference = (value: unknown): value is TTemplateEntityReference =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// The display text a single input value contributes to generated request text.
// An entity reference resolves to its label; a multi-value input joins with commas.
const inputValueToText = (value: TTemplateInputValue | undefined): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value))
    return value
      .map((entry) => (isEntityReference(entry) ? entry.label.trim() : entry.trim()))
      .filter(Boolean)
      .join(', ');
  if (isEntityReference(value)) return value.label.trim();
  return '';
};

// Collapses the duplicate spaces and dangling punctuation an empty optional value
// leaves behind (e.g. "for . Next" -> "for. Next", "a,  b" -> "a, b").
const normalizeWhitespace = (value: string): string =>
  value
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/([([])\s+/g, '$1')
    .replace(/\s+([)\]])/g, '$1')
    .replace(/([.,;:])\1+/g, '$1')
    .trim();

export type TTemplateResolution = {
  text: string;
  // Required inputs whose token appeared but had no value. In create mode these
  // block draft creation; in preview mode they render as `[Label]`.
  missingRequired: TTemplateCreationInputConfig[];
  // Token ids with no matching input config. A configuration error: warned in
  // development and treated as blocking for draft creation.
  unknownTokens: string[];
};

// Resolves every `{{input:...}}` token in a single string. Exposed as the reusable
// primitive; callers that only need the text use `resolveTemplateString`.
export const resolveTemplateStringDetailed = (
  value: string,
  inputValues: Record<string, TTemplateInputValue>,
  inputConfigs: TTemplateCreationInputConfig[],
  mode: TResolveMode
): TTemplateResolution => {
  const missingRequired: TTemplateCreationInputConfig[] = [];
  const unknownTokens: string[] = [];

  const text = value.replace(TOKEN_PATTERN, (_match, inputId: string) => {
    const config = inputConfigs.find((input) => input.id === inputId);
    if (!config) {
      unknownTokens.push(inputId);
      // A configuration error: warn developers. Draft creation is blocked upstream
      // when unknownTokens is non-empty.
      // eslint-disable-next-line no-console
      console.warn(`resolveTemplateString: unknown template input token "${inputId}".`);
      // Never surface raw token syntax to the user; drop the unknown token.
      return '';
    }

    const resolved = inputValueToText(inputValues[inputId]);
    if (resolved) return resolved;

    if (config.required) {
      if (!missingRequired.some((entry) => entry.id === config.id)) missingRequired.push(config);
      // A neutral placeholder in preview; an empty slot at creation (blocked anyway).
      return mode === 'preview' ? `[${config.label}]` : '';
    }
    // Unresolved optional token becomes an empty string.
    return '';
  });

  return { text: normalizeWhitespace(text), missingRequired, unknownTokens };
};

// Resolves tokens using only a value map (no input configs). Used when an
// assessment is instantiated from a Change's stored input snapshot: every token
// with a value is replaced, tokens without a value are dropped, and a string with
// no token is returned unchanged. Raw token syntax is never emitted.
export const resolveTokensFromValues = (value: string, inputValues: Record<string, TTemplateInputValue>): string => {
  if (!value.includes('{{input:')) return value;
  return normalizeWhitespace(
    value.replace(TOKEN_PATTERN, (_match, inputId: string) => inputValueToText(inputValues[inputId]))
  );
};

// Reusable helper per the prompt. Replaces every known token with its value,
// renders missing required tokens as `[Field label]` in preview mode, and returns
// plain text (never HTML, never raw token syntax).
export const resolveTemplateString = (
  value: string,
  inputValues: Record<string, TTemplateInputValue>,
  inputConfigs: TTemplateCreationInputConfig[],
  mode: TResolveMode
): string => resolveTemplateStringDetailed(value, inputValues, inputConfigs, mode).text;

export type TResolvedRequestPrefills = {
  title: string;
  description: string;
  reasonOrTrigger: string;
  desiredOutcome: string;
  implementationProposal: string;
  scopeType?: TChangeTemplate['requestPrefills']['scopeType'];
  urgency?: TChangeTemplate['requestPrefills']['urgency'];
  changeCategory?: TChangeTemplate['requestPrefills']['changeCategory'];
  affectedQmsAreas: string[];
};

// Resolves the request text fields a template prefills. Non-text prefills (scope,
// urgency, category, QMS areas) are enum/technical values and are never tokenized.
export const resolveRequestPrefills = (
  template: TChangeTemplate,
  inputValues: Record<string, TTemplateInputValue>,
  mode: TResolveMode
): TResolvedRequestPrefills => {
  const inputs = template.creationInputs ?? [];
  const prefills = template.requestPrefills;
  const resolve = (value?: string) => (value ? resolveTemplateString(value, inputValues, inputs, mode) : '');
  return {
    title: resolve(prefills.title),
    description: resolve(prefills.description),
    reasonOrTrigger: resolve(prefills.reasonOrTrigger),
    desiredOutcome: resolve(prefills.desiredOutcome),
    implementationProposal: resolve(prefills.implementationProposal),
    scopeType: prefills.scopeType,
    urgency: prefills.urgency,
    changeCategory: prefills.changeCategory,
    affectedQmsAreas: prefills.affectedQmsAreas ?? [],
  };
};

// Whether a single creation-input value counts as provided (for required checks).
export const isInputProvided = (value: TTemplateInputValue | undefined): boolean => inputValueToText(value).length > 0;

// The required creation inputs that are still empty. Draft creation is disabled
// while this is non-empty.
export const getMissingRequiredInputs = (
  inputConfigs: TTemplateCreationInputConfig[],
  inputValues: Record<string, TTemplateInputValue>
): TTemplateCreationInputConfig[] =>
  inputConfigs.filter((input) => input.required && !isInputProvided(inputValues[input.id]));

// The tokenized strings a template resolves for the creation preview and draft.
const templateResolvableStrings = (template: TChangeTemplate): string[] =>
  [
    template.requestPrefills.title,
    template.requestPrefills.description,
    template.requestPrefills.reasonOrTrigger,
    template.requestPrefills.desiredOutcome,
    template.requestPrefills.implementationProposal,
    ...template.suggestedTasks.map((task) => task.title),
    ...template.suggestedTasks.flatMap((task) => task.documents?.map((document) => document.name) ?? []),
  ].filter((value): value is string => Boolean(value));

// Token ids used by a template that have no matching creation-input config. Any
// such token is a configuration error and blocks draft creation.
export const getUnknownTemplateTokens = (
  template: TChangeTemplate,
  inputValues: Record<string, TTemplateInputValue>
): string[] => {
  const inputs = template.creationInputs ?? [];
  const unknown = new Set<string>();
  for (const value of templateResolvableStrings(template)) {
    for (const token of resolveTemplateStringDetailed(value, inputValues, inputs, 'create').unknownTokens) {
      unknown.add(token);
    }
  }
  return [...unknown];
};
